import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { AAG_SOD_DETAILS, AAG_SOD_ENT_DETAILS } from '../constants';
import { detailsActions } from '../store/actions';
import {
  type DetailsNavigationParams,
  type NavigateToDetailsProps,
} from '../types/navigationTypes';

export type { DetailsNavigationParams, NavigateToDetailsProps };

/**
 * A custom hook that provides navigation functionality.
 *
 * @description
 * This hook wraps the navigation logic and provides a consistent way to navigate
 * throughout the application. It can handle both string paths and object-based
 * navigation details. Additionally, you can pass optional state data when navigating,
 * which can be accessed in the target component via `useLocation`.
 *
 * @example
 * // Navigate using a string path
 * const { navigateToUrl } = useNavigateTo();
 * navigateToUrl('/dashboard');
 *
 * @example
 * // Navigate to details with activeTab only in the URL
 * const { navigateToDetails } = useNavigateTo();
 * navigateToDetails({
 *   key: '123',
 *   activeTab: 'conflicts',
 *   navigation: { source: '/soddetail/violations', title: 'Risk', userName: 'user1' },
 * });
 *
 * @example
 * // Go back to previous page
 * const { navigateToBack } = useNavigateTo();
 * navigateToBack();
 *
 * @example
 * // Update only the activeTab param on current page
 * const { updateActiveTab } = useNavigateTo();
 * updateActiveTab('mitigate');
 *
 * @returns {Function} returns.updateActiveTab - Updates the activeTab param on the current URL without adding history.
 *
 * @returns {Object} An object containing navigation functions:
 * @returns {Function} returns.navigateToDetails - Navigate to details page with optional params and state.
 * @returns {Function} returns.navigateToUrl - Navigate using a direct URL string.
 * @returns {Function} returns.navigateToBack - Navigate back to the previous page.
 */
const useNavigateTo = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const navigateToDetails = useCallback(
    ({
      key,
      activeTab,
      isEntitlementRoute,
      navigation,
    }: NavigateToDetailsProps): void => {
      if (navigation) {
        dispatch(detailsActions.setDetailsNavigation(navigation));
      }

      const search = activeTab
        ? `?${new URLSearchParams({ activeTab }).toString()}`
        : '';

      const basePath = isEntitlementRoute
        ? AAG_SOD_ENT_DETAILS
        : AAG_SOD_DETAILS;

      history.push({
        pathname: `${basePath}/${key}`,
        search,
      });
    },
    [dispatch, history]
  );

  const updateActiveTab = useCallback(
    (value: string): void => {
      const search = value
        ? `?${new URLSearchParams({ activeTab: value }).toString()}`
        : '';

      history.replace({
        pathname: history.location.pathname,
        search,
      });
    },
    [history]
  );

  const navigateToUrl = useCallback(
    (url: string): void => {
      history.push(url);
    },
    [history]
  );

  const navigateToBack = useCallback((): void => {
    history.goBack();
  }, [history]);

  return { navigateToDetails, updateActiveTab, navigateToUrl, navigateToBack };
};

// eslint-disable-next-line import/prefer-default-export
export { useNavigateTo };
